import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { eur, eurSigned, km, num, pct, dateOnly, dateTime, NA } from "@/lib/format";
import { InlineWarning, ListingLinkButton, ScoreBadge } from "@/components/common/Primitives";
import { ListPricePanel } from "@/components/bpm/ListPricePanel";
import { TariffVariants } from "@/components/bpm/TariffVariants";
import { api, apiError } from "@/lib/api";

const Row = ({ label, value, note, strong, tone }) => (
  <div
    className={`flex items-start justify-between gap-3 border-b border-slate-100 px-3 py-1.5 ${
      strong ? "bg-slate-50" : ""
    }`}
  >
    <div className="min-w-0">
      <div className={`text-[12px] ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {label}
      </div>
      {note && <div className="text-[10px] leading-tight text-slate-500">{note}</div>}
    </div>
    <div
      className={`mono shrink-0 text-right text-[12px] ${
        tone === "positive"
          ? "font-semibold text-emerald-700"
          : tone === "danger"
            ? "font-semibold text-red-700"
            : strong
              ? "font-semibold text-slate-900"
              : "text-slate-800"
      }`}
    >
      {value}
    </div>
  </div>
);

export const DialogLoading = () => (
  <div
    className="flex items-center justify-center gap-2 py-12 text-[12px] text-slate-500"
    data-testid="dialog-loading"
  >
    <Loader2 className="h-4 w-4 animate-spin" /> Analyse laden…
  </div>
);

const EXCLUDE_CATEGORIES = [
  [/ander model|model komt|niet in de taxonomie/i, "ander model"],
  [/generatie/i, "andere generatie"],
  [/deuren/i, "ander aantal deuren"],
  [/toelating|bouwjaar/i, "te groot leeftijdsverschil"],
  [/kilometer|\bkm\b/i, "te groot km-verschil"],
  [/brandstof/i, "andere brandstof"],
  [/carrosserie|body/i, "andere carrosserie"],
  [/transmissie|versnelling/i, "andere transmissie"],
  [/vermogen|\bpk\b|\bkw\b/i, "ander vermogen"],
  [/uitvoering|vergelijkbaarheid|te lage/i, "andere uitvoering"],
  [/dubbel|duplicaat/i, "duplicaat"],
];

const excludedReasonSummary = (excluded) => {
  const counts = {};
  (excluded || []).forEach((c) => {
    const reason = c.excluded_reason || "overig";
    const hit = EXCLUDE_CATEGORIES.find(([re]) => re.test(reason));
    const label = hit ? hit[1] : "overig";
    counts[label] = (counts[label] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6);
};

export const Co2SourcePanel = ({ co2, bpm, compact = false }) => {
  const source = bpm?.co2_source || null;
  const confidence = co2?.confidence ?? bpm?.co2_confidence;
  const status = co2?.confidence_status || bpm?.co2_confidence_status;
  const tone =
    status === "EXACT" || status === "HIGH_CONFIDENCE"
      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
      : status === "ESTIMATED"
        ? "border-amber-300 bg-amber-50 text-amber-900"
        : status === "LOW_CONFIDENCE"
          ? "border-orange-300 bg-orange-50 text-orange-900"
          : "border-red-300 bg-red-50 text-red-800";
  return (
    <div className="border border-slate-200" data-testid="co2-source-panel">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-100 px-3 py-1.5">
        <span className="label-xs">CO₂-bron en matchkwaliteit</span>
        <span className={`chip ${tone}`} data-testid="co2-confidence-chip">
          {(co2?.confidence_label || CO2_STATUS_LABEL[status] || "Geen bron").toUpperCase()}
          {confidence ? ` · ${Math.round(confidence)}%` : ""}
        </span>
      </div>
      {!co2?.co2 && !source ? (
        <div className="px-3 py-2 text-[12px] text-red-800">
          Geen CO₂-bron gevonden. Er wordt géén waarde ingevuld en de BPM blijft onbepaald.
        </div>
      ) : (
        <>
          <Row
            label="CO₂"
            value={
              co2?.co2_min && co2?.co2_max && co2.co2_min !== co2.co2_max
                ? `${num(co2.co2_min)} – ${num(co2.co2_max)} g/km`
                : `${num(co2?.co2 ?? bpm?.co2_used)} g/km`
            }
            note={
              co2?.co2_most_common && co2?.co2_min !== co2?.co2_max
                ? `Meest waarschijnlijk ${num(co2.co2_most_common)} g/km`
                : undefined
            }
            strong
          />
          <Row label="Meetmethode" value={co2?.measurement_method || bpm?.co2_measurement_method || NA} />
          <Row label="Bron" value={co2?.source_name || source?.source_name || NA}
               note={SOURCE_TYPE_LABEL[co2?.source_type] || co2?.source_type} />
          <Row label="Referentievoertuigen" value={num(co2?.reference_vehicle_count ?? 0)} />
          <Row label="Specificatiematch" value={co2?.match_criteria || source?.match_criteria || NA}
               note={co2?.match_score ? `Matchscore ${co2.match_score}` : undefined} />
          <Row label="Opgehaald" value={dateTime(co2?.retrieved_at || source?.retrieved_at)} />
          {co2?.source_url && (
            <div className="px-3 py-1.5">
              <a
                href={co2.source_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="co2-source-url"
                className="text-[11px] font-semibold text-blue-700 hover:underline"
              >
                Bron openen
              </a>
            </div>
          )}
          {!compact && (co2?.distribution || []).length > 0 && (
            <div className="px-3 py-2">
              <div className="label-xs mb-1">CO₂-verdeling referentievoertuigen</div>
              <div className="flex flex-wrap gap-1">
                {co2.distribution.map((d) => (
                  <span key={d.co2} className="chip border-slate-300 bg-slate-50 text-slate-700">
                    {num(d.co2)} g/km × {d.count}
                  </span>
                ))}
              </div>
            </div>
          )}
          {(co2?.match_reasons || []).length > 0 && !compact && (
            <div className="border-t border-slate-100 px-3 py-2">
              <div className="label-xs mb-1">Matchcriteria</div>
              <ul className="space-y-0.5">
                {co2.match_reasons.map((r) => (
                  <li key={r} className="text-[11px] text-slate-600">• {r}</li>
                ))}
              </ul>
            </div>
          )}
          {(co2?.conflicts || []).length > 0 && (
            <div className="px-3 py-2">
              {co2.conflicts.map((c) => (
                <InlineWarning key={c.message} tone="danger" testid="co2-conflict">
                  Bronnen conflicteren: {c.message}
                </InlineWarning>
              ))}
            </div>
          )}
        </>
      )}
      {(co2?.searched_sources || []).length > 0 && !compact && (
        <div className="border-t border-slate-100 px-3 py-2">
          <div className="label-xs mb-1">Doorzochte bronnen (prioriteitsvolgorde)</div>
          {co2.searched_sources.map((s) => (
            <div key={s.key || s.label} className="flex items-start justify-between gap-2 py-0.5">
              <span className="text-[11px] text-slate-700">{s.label}</span>
              <span className="text-[10px] text-slate-500">
                {SEARCH_STATUS_LABEL[s.status] || s.status}
                {s.count ? ` · ${s.count}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CO2_STATUS_LABEL = {
  EXACT: "Exact",
  HIGH_CONFIDENCE: "Hoge betrouwbaarheid",
  ESTIMATED: "Geschat",
  LOW_CONFIDENCE: "Lage betrouwbaarheid",
  MANUAL_REVIEW_REQUIRED: "Handmatige controle vereist",
};

const SOURCE_TYPE_LABEL = {
  RDW_REFERENCE: "RDW referentievoertuigen (officieel)",
  ADVERTENTIE: "CO₂ uit de advertentie zelf",
  MANUAL_OVERRIDE: "Handmatige invoer (gebruiker)",
  EU_DATASET: "Europese CO₂-dataset",
  MANUFACTURER: "Fabrikant / typegoedkeuring",
  SPEC_DATABASE: "Specificatiedatabase",
  WEB_SOURCE: "Webbron",
};

const SEARCH_STATUS_LABEL = {
  HIT: "hergebruikt", MISS: "geen match", CONNECTED: "doorzocht",
  NOT_CONFIGURED: "niet gekoppeld", ERROR: "fout", SKIPPED: "overgeslagen",
};

export const BpmDialog = ({ open, onOpenChange, bpm, co2, listingId, onChanged }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-3xl bg-white" data-testid="bpm-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-base">BPM-berekening</DialogTitle>
      </DialogHeader>
      {!bpm ? <DialogLoading /> : (
        <div className="max-h-[70vh] space-y-3 overflow-y-auto">
          {bpm.message && <InlineWarning tone="danger">{bpm.message}</InlineWarning>}
          {bpm.manual_override && (
            <div className="border border-blue-300" data-testid="bpm-manual-panel">
              <div className="label-xs border-b border-blue-300 bg-blue-50 px-3 py-1.5 text-blue-900">
                Handmatige BPM — heeft voorrang op de CO₂-berekening
              </div>
              <Row
                label="Automatisch berekende BPM (CO₂)"
                note={bpm.automatic?.status_label}
                value={eur(bpm.automatic?.bpm_estimate ?? bpm.automatic_bpm, {
                  fallback: "Niet te bepalen",
                })}
              />
              <Row label="Handmatige BPM" value={eur(bpm.manual_bpm)} />
              <Row label="Gebruikte BPM" value={eur(bpm.bpm_estimate)} strong tone="positive" />
              <Row
                label="Toegepast op"
                note={bpm.override?.group_label}
                value={bpm.override?.scope === "GROUP" ? "Hele technische groep" : "Alleen deze auto"}
              />
            </div>
          )}
          <Co2SourcePanel co2={co2} bpm={bpm} />
          <div className="border border-slate-200">
            <Row label="Status" value={bpm.status_label} />
            <Row label="CO₂ gebruikt" value={bpm.co2_used === null || bpm.co2_used === undefined ? NA : `${num(bpm.co2_used)} g/km`} note={bpm.co2_basis} />
            <Row
              label="Gekozen tariefperiode"
              value={bpm.tariff_period?.label || bpm.tariff_year || NA}
              note={bpm.tariff_source}
            />
            <Row label="Methode" value={bpm.method} />
            <Row label="Leeftijd" value={bpm.age_months === null || bpm.age_months === undefined ? NA : `${bpm.age_months} maanden`} />
          </div>
          <ListPricePanel bpm={bpm} listingId={listingId} onChanged={onChanged} />
          <TariffVariants bpm={bpm} />
          <div className="border border-slate-200">
            <Row
              label="Afschrijving (forfaitaire tabel)"
              note={`${pct(bpm.depreciation_pct)} van bruto BPM`}
              value={bpm.depreciation_amount === null ? NA : `− ${eur(bpm.depreciation_amount)}`}
            />
            <Row label="Rest-BPM" value={eur(bpm.remaining_bpm)} strong />
            {bpm.has_range ? (
              <>
                <Row label="BPM laag (CO₂ minimum)" value={eur(bpm.bpm_low)} />
                <Row label="BPM verwacht (meest waarschijnlijke CO₂)" value={eur(bpm.bpm_expected)} />
                <Row label="BPM hoog (CO₂ maximum)" value={eur(bpm.bpm_high)} strong />
                <Row
                  label="Gebruikt in handelsberekening"
                  note={bpm.conservative_basis
                    ? "Conservatief: hoogste BPM, zodat de handelsmarge niet te rooskleurig wordt"
                    : "Verwachte BPM"}
                  value={eur(bpm.bpm_estimate)}
                  strong
                />
              </>
            ) : (
              <Row
                label="BPM-indicatie (bandbreedte)"
                value={
                  bpm.bpm_range_low === null
                    ? NA
                    : `${eur(bpm.bpm_range_low)} – ${eur(bpm.bpm_range_high)}`
                }
              />
            )}
          </div>
          {(bpm.missing_fields || []).length > 0 && (
            <InlineWarning>Ontbrekende gegevens: {bpm.missing_fields.join(", ")}</InlineWarning>
          )}
          {(bpm.notes || []).map((n) => (
            <p key={n} className="text-[11px] text-slate-500">
              • {n}
            </p>
          ))}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const ValuationDialog = ({ open, onOpenChange, valuation, listingId, onChanged }) => {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const basis = valuation?.basis || {};

  const mutate = async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      const { data } = await fn();
      onChanged?.(data);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  const exclude = (comparableId) =>
    mutate(comparableId, () =>
      api.post(`/listings/${listingId}/comparables/${comparableId}/exclude`, {
        reason: "Niet vergelijkbaar volgens gebruiker",
      })
    );

  const restore = (comparableId) =>
    mutate(comparableId, () =>
      api.delete(`/listings/${listingId}/comparables/${comparableId}/exclude`)
    );

  const findComparables = async () => {
    setBusy("find");
    setError("");
    try {
      await api.post(`/listings/${listingId}/find-comparables`);
      let done = false;
      for (let i = 0; i < 40 && !done; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const { data } = await api.get(`/listings/${listingId}/find-comparables/status`);
        if (data.status === "DONE" || data.status === "ERROR") {
          done = true;
          if (data.status === "ERROR") setError(data.message || "Live zoeken mislukt.");
        }
      }
      const { data: fresh } = await api.get(`/listings/${listingId}/analysis`);
      onChanged?.(fresh);
    } catch (e) {
      setError(apiError(e));
    }
    setBusy("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl bg-white" data-testid="valuation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-base">Waardering — onderbouwing</DialogTitle>
        </DialogHeader>
        {!valuation ? <DialogLoading /> : (
          <div className="max-h-[70vh] space-y-3 overflow-y-auto">
            {error && <InlineWarning tone="danger">{error}</InlineWarning>}
            {(valuation.warnings || []).map((w, i) => (
              <InlineWarning key={w} tone={i === 0 ? "danger" : "warning"}>
                {w}
              </InlineWarning>
            ))}
            {listingId && (valuation.n_used || 0) === 0 && (
              <div className="flex items-center justify-between gap-3 border border-red-200 bg-red-50 px-3 py-2">
                <span className="text-[12px] text-red-800">
                  Geen Nederlandse vergelijkers in de database. Zoek live op AutoScout24 NL.
                </span>
                <button
                  type="button"
                  onClick={findComparables}
                  disabled={busy !== ""}
                  data-testid="find-comparables-btn"
                  className="shrink-0 rounded-sm bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors duration-150 hover:bg-red-700 disabled:opacity-50"
                >
                  {busy === "find" ? "Bezig met live zoeken…" : "Zoek live NL-vergelijkers"}
                </button>
              </div>
            )}
            <div className="border border-slate-200">
              <Row label="Methode" value={valuation.method} />
              {valuation.value_source === "MANUAL" && (
                <Row
                  label="Automatisch berekende marktwaarde"
                  value={eur(valuation.automatic_expected_market_value, {
                    fallback: "Niet te bepalen",
                  })}
                />
              )}
              <Row label="Snelle verkoopwaarde" value={eur(valuation.quick_sale_value)} />
              <Row
                label={
                  valuation.value_source === "MANUAL"
                    ? "Verwachte marktwaarde (HANDMATIG)"
                    : "Verwachte marktwaarde"
                }
                value={eur(valuation.expected_market_value)}
                note={
                  valuation.expected_market_value
                    ? `incl. 21% BTW · netto-opbrengst ${eur(Math.round(valuation.expected_market_value / 1.21))}`
                    : undefined
                }
                strong
              />
              <Row label="Optimistische waarde" value={eur(valuation.optimistic_value)} />
              <Row label="Mediaan vraagprijs" value={eur(basis.median_asking_price)} />
              <Row
                label="Kilometercorrectie"
                note={
                  basis.mileage_pct_per_10000km
                    ? `${basis.mileage_learned ? "Geleerd uit marktdata" : "Standaardaanname"}: ${
                        basis.mileage_pct_per_10000km
                      }% per 10.000 km`
                    : undefined
                }
                value={eurSigned(basis.mileage_correction, { fallback: "—" })}
              />
              <Row
                label="Bouwjaarcorrectie"
                note={
                  basis.age_pct_per_month
                    ? `${basis.age_learned ? "Geleerd uit marktdata" : "Standaardaanname"}: ${
                        basis.age_pct_per_month
                      }% per maand`
                    : undefined
                }
                value={eurSigned(basis.year_correction, { fallback: "—" })}
              />
              <Row label="Mediaan (gecorrigeerd)" value={eur(valuation.raw_median)} />
              <Row label="P25 / P75" value={`${eur(valuation.p25)} / ${eur(valuation.p75)}`} />
              <Row label="Prijsspreiding" value={pct(valuation.dispersion_pct)} />
              <Row label="Voertuigidentiteit" value={basis.identity || basis.core || NA} />
              <Row label="Gebruikte zoekstap" value={valuation.stage_label || NA} />
            </div>
            {(valuation.search_steps || []).length > 0 && (
              <div className="border border-slate-200">
                <div className="label-xs border-b border-slate-200 bg-slate-100 px-3 py-1.5">
                  Zoekstappen
                </div>
                {valuation.search_steps.map((s) => (
                  <Row key={s.step} label={s.label} value={`${s.found} gevonden`} />
                ))}
              </div>
            )}
            <div className="border border-slate-200">
              <div className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 label-xs">
                Betrouwbaarheid {valuation.confidence_pct}% — {valuation.confidence_label}
              </div>
              {Object.entries(valuation.confidence_components || {}).map(([key, comp]) => (
                <Row
                  key={key}
                  label={key.replaceAll("_", " ")}
                  note={`waarde: ${comp.value}`}
                  value={`${num(comp.points, { decimals: 1 })} / ${comp.max}`}
                />
              ))}
            </div>
            <div className="border border-slate-200">
              <div className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 label-xs">
                Gebruikte vergelijkingsauto's ({valuation.n_used})
              </div>
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Voertuig</th>
                      <th>1e toelating</th>
                      <th>KM</th>
                      <th>Vraagprijs</th>
                      <th>Correctie</th>
                      <th>Gecorrigeerd</th>
                      <th>Score</th>
                      <th>Gewicht</th>
                      <th>URL</th>
                      {listingId && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {(valuation.comparables_used || []).map((c) => (
                      <tr key={c.listing_id}>
                        <td className="max-w-[200px] truncate">
                          {[c.make, c.model, c.variant].filter(Boolean).join(" ")}
                        </td>
                        <td className="num">{dateOnly(c.first_registration)}</td>
                        <td className="num">{km(c.mileage_km)}</td>
                        <td className="num">{eur(c.price)}</td>
                        <td className="num">{eurSigned(c.total_adjustment)}</td>
                        <td className="num font-semibold">{eur(c.adjusted_price)}</td>
                        <td className="num">{num(c.comparison_score, { decimals: 1 })}</td>
                        <td className="num">{num(c.weight, { decimals: 2 })}</td>
                        <td>
                          <ListingLinkButton url={c.url} testid={`comp-url-${c.listing_id}`} />
                        </td>
                        {listingId && (
                          <td>
                            <button
                              type="button"
                              onClick={() => exclude(c.listing_id)}
                              disabled={busy !== ""}
                              data-testid={`vd-exclude-${c.listing_id}`}
                              className="rounded-sm border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 transition-colors duration-150 hover:border-red-400 hover:text-red-700"
                            >
                              Niet vergelijkbaar
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {(valuation.comparables_excluded || []).length > 0 && (
              <div className="border border-slate-200">
                <div className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 label-xs">
                  Uitgesloten ({valuation.comparables_excluded_total || valuation.comparables_excluded.length})
                </div>
                {excludedReasonSummary(valuation.comparables_excluded).length > 0 && (
                  <div
                    className="flex flex-wrap gap-x-3 gap-y-1 border-b border-slate-100 px-3 py-1.5 text-[10px] text-slate-500"
                    data-testid="excluded-summary"
                  >
                    {excludedReasonSummary(valuation.comparables_excluded).map((s) => (
                      <span key={s.label}>
                        <span className="font-semibold text-slate-700">{s.count}×</span> {s.label}
                      </span>
                    ))}
                  </div>
                )}
                {valuation.comparables_excluded.map((c, i) => (
                  <div
                    key={`${c.listing_id || i}`}
                    className="flex items-center justify-between gap-3 border-b border-slate-100 px-3 py-1.5"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-[12px] text-slate-700">
                        {[c.make, c.model, c.variant].filter(Boolean).join(" ") || "Onbekend"}
                      </div>
                      <div className="text-[10px] text-slate-500">{c.excluded_reason}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="mono text-[12px] text-slate-800">{eur(c.price)}</span>
                      {listingId && c.user_excluded && (
                        <button
                          type="button"
                          onClick={() => restore(c.listing_id)}
                          disabled={busy !== ""}
                          data-testid={`vd-restore-${c.listing_id}`}
                          className="rounded-sm border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:border-blue-400 hover:text-blue-700"
                        >
                          Terugzetten
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ScoreDialog = ({ open, onOpenChange, score }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xl bg-white" data-testid="score-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-base">Deal Score — score-opbouw</DialogTitle>
      </DialogHeader>
      {!score ? <DialogLoading /> : (
        <div className="max-h-[70vh] space-y-3 overflow-y-auto">
          <div className="flex items-center gap-3">
            <ScoreBadge score={score.score} advice={score.advice} size="lg" />
            <span className="text-[12px] text-slate-600">{score.label}</span>
          </div>
          {score.message && <InlineWarning tone="danger">{score.message}</InlineWarning>}
          <div className="border border-slate-200">
            {Object.entries(score.components || {}).map(([key, comp]) => (
              <div key={key} className="border-b border-slate-100 px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-semibold capitalize text-slate-800">
                    {key.replaceAll("_", " ")}
                  </span>
                  <span className="mono text-[12px] font-semibold text-slate-900">
                    {num(comp.points, { decimals: 1 })} / {comp.max}
                  </span>
                </div>
                <div className="mt-1 h-1 w-full overflow-hidden rounded-sm bg-slate-200">
                  <div
                    className="h-full bg-blue-600"
                    style={{ width: `${Math.max(0, (comp.points / comp.max) * 100)}%` }}
                  />
                </div>
                <ul className="mt-1 space-y-0.5">
                  {(comp.notes || []).map((n) => (
                    <li key={n} className="text-[10.5px] leading-tight text-slate-500">
                      {n}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500">
            Deterministisch gewogen model. De AI-laag interpreteert deze score maar berekent hem
            niet.
          </p>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const CostDialog = ({ open, onOpenChange, costs, scenario }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xl bg-white" data-testid="cost-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-base">Kostenopbouw — landed cost</DialogTitle>
      </DialogHeader>
      {!costs ? <DialogLoading /> : (
        <div className="max-h-[70vh] space-y-3 overflow-y-auto">
          {costs.vat?.warning && <InlineWarning>{costs.vat.warning}</InlineWarning>}
          <div className="border border-slate-200">
            {costs.lines.map((line) => (
              <Row
                key={line.key}
                label={
                  <span className="flex items-center gap-1.5">
                    {line.label}
                    {line.assumption && (
                      <span className="chip border-slate-300 bg-slate-100 text-[9px] text-slate-500">
                        aanname
                      </span>
                    )}
                    {line.overridden && (
                      <span className="chip border-blue-300 bg-blue-50 text-[9px] text-blue-700">
                        aangepast
                      </span>
                    )}
                  </span>
                }
                note={line.unresolved ? line.unresolved_message : line.note}
                value={line.amount === null ? "Niet berekenbaar" : eur(line.amount)}
                tone={line.unresolved ? "danger" : undefined}
              />
            ))}
            <Row
              label="Subtotaal (voor onvoorzien)"
              value={eur(costs.landed_cost_before_contingency)}
              strong
            />
            <Row
              label={costs.contingency?.label}
              note="Standaard aanname — configureerbaar"
              value={eur(costs.contingency?.amount)}
            />
            <Row label="TOTALE LANDED COST" value={eur(costs.landed_cost)} strong />
          </div>
          {costs.vat && (
          <div className="border border-slate-200">
            <div className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 label-xs">
              BTW-behandeling
            </div>
            <Row label="Status" value={costs.vat.status} />
            <Row label="Bruto inkoopprijs" value={eur(costs.vat.gross_purchase_price)} />
            <Row label="Netto inkoopprijs" value={eur(costs.vat.net_purchase_price)} />
            <Row label="BTW-bedrag" value={eur(costs.vat.vat_amount)} />
            <Row
              label="BTW terugvorderbaar"
              value={costs.vat.vat_recoverable ? "Ja" : "Nee"}
              note={costs.vat.conservative ? "Conservatief berekend" : undefined}
            />
            <Row
              label="Werkelijke kasuitgave inkoop"
              value={eur(costs.vat.cash_outlay)}
              note="Inclusief eventueel voor te schieten BTW"
            />
          </div>
          )}
          {scenario && (
            <div className="border border-slate-200">
              <div className="border-b border-slate-200 bg-slate-100 px-3 py-1.5 label-xs">
                Resultaat (basisscenario)
              </div>
              <Row label="Verkoopwaarde NL (incl. BTW)" value={eur(scenario.sale_value)} />
              <Row
                label={`− Verkoop-BTW NL (${scenario.sales_vat_pct ?? 21}%)`}
                value={scenario.sales_vat != null ? `− ${eur(scenario.sales_vat)}` : "—"}
                tone="danger"
              />
              <Row label="Netto-opbrengst NL" value={eur(scenario.net_sale_value)} strong />
              <Row label="Landed cost" value={`− ${eur(scenario.landed_cost)}`} />
              <Row
                label="Potentiële handelsbijdrage"
                value={eurSigned(scenario.contribution)}
                strong
                tone={scenario.contribution > 0 ? "positive" : "danger"}
              />
              <Row label="ROI" value={pct(scenario.roi_pct)} />
              <Row
                label="Handelsbijdrage vóór onvoorzien"
                value={eurSigned(scenario.contribution_before_contingency)}
              />
              <Row label="Break-even verkoopprijs" value={eur(scenario.break_even_price)} />
            </div>
          )}
          <p className="text-[11px] text-slate-500">
            De Nederlandse verkoop-BTW is al afgetrokken van de verkoopwaarde. Dit is een{" "}
            <strong>potentiële handelsbijdrage</strong>, geen netto winst: vaste bedrijfskosten,
            financieringslasten en inkomsten-/vennootschapsbelasting zijn niet meegenomen.
          </p>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const FreshnessRow = ({ timestamps }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500">
    <span>
      Brondata bijgewerkt:{" "}
      <span className="mono text-slate-700">{dateTime(timestamps?.source_data_updated)}</span>
    </span>
    <span>
      NL marktvergelijking:{" "}
      <span className="mono text-slate-700">{dateTime(timestamps?.comparables_calculated)}</span>
    </span>
    <span>
      BPM berekend:{" "}
      <span className="mono text-slate-700">{dateTime(timestamps?.bpm_calculated)}</span>
    </span>
  </div>
);
